import numeral from "numeral";
import { isAxiosError } from "axios";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useCallback, useEffect, useState } from "react";
import { useAsync } from "react-use";
import { useMessage } from "@/contexts";
import {
  TransferCategory,
  TransferCreate,
  TransferRoundHelper,
  TransferService,
} from "@/domains/transfer";
import { UserService } from "@/domains/user";
// components
import { Button, Checkbox, Form, Input, Spin } from "antd";

export const TenantManageCreateMemberWithdrawPage = () => {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const { messageApi } = useMessage();

  const [form] = Form.useForm<TransferCreate>();

  const [loading, setLoading] = useState(false);

  const [isSyncToAmount, setIsSyncToAmount] = useState(true);

  const [isSyncToAmount0, setIsSyncToAmount0] = useState(false);

  const withdraw_rate = useAsync(async () => {
    const res = await UserService.getMyRates().run();
    return res.data.msg.withdraw_rate;
  });

  const withdraw_round = useAsync(async () => {
    const res = await UserService.getMyRounds().run();
    return res.data.msg.withdraw_round;
  });

  const isWithdrawRateRequired =
    withdraw_rate.value !== undefined && parseFloat(withdraw_rate.value) !== 1;

  const handleAmount0Input = useCallback(
    (value: string) => {
      if (!isSyncToAmount || !withdraw_round.value) return;

      if (Number.isNaN(parseFloat(value))) {
        form.setFieldValue("amount", "");
        return;
      }

      const amount = numeral(value).divide(withdraw_rate.value).value();
      if (amount === null) return;

      const newAmount = TransferRoundHelper.round(amount, withdraw_round.value);
      form.setFieldValue("amount", newAmount);
    },
    [form, isSyncToAmount, withdraw_rate.value, withdraw_round.value]
  );

  const handleAmountInput = useCallback(
    (value: string) => {
      if (!isSyncToAmount0 || !isWithdrawRateRequired) return;

      if (Number.isNaN(parseFloat(value))) {
        form.setFieldValue("amount0", "");
        return;
      }

      const amount0 = numeral(value).multiply(withdraw_rate.value).value();
      if (amount0 === null) return;

      form.setFieldValue("amount0", Math.ceil(amount0));
    },
    [form, isSyncToAmount0, isWithdrawRateRequired, withdraw_rate.value]
  );

  const handleFinish = async (values: TransferCreate) => {
    const data = {
      ...values,
      currency: "USDT",
    };

    try {
      setLoading(true);
      await TransferService.create({
        ...data,
        category: TransferCategory.MemberWithdraw,
        currency: "USDT",
      }).run();
      void messageApi.success(t("systems.Modal.memberWithdrawCreated"));
      navigate("/tenant/reports/member-withdraw");
    } catch (e) {
      if (isAxiosError(e)) {
        void messageApi.error(e.response?.data.msg as string);
      }
      const res = await UserService.getMyRates().run();
      form.setFieldValue("rate", res.data.msg.withdraw_rate);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const field = form.getFieldInstance("amount0");
    if (!field) return;
    const input = form.getFieldInstance("amount0").input as HTMLInputElement;
    handleAmount0Input(input.value);
  }, [form, handleAmount0Input]);

  useEffect(() => {
    const field = form.getFieldInstance("amount");
    if (!field) return;
    const input = form.getFieldInstance("amount").input as HTMLInputElement;
    handleAmountInput(input.value);
  }, [form, handleAmountInput]);

  const Amount0FormItem = () => (
    <Form.Item
      label={t("domains.Transfer.withdrawAmount")}
      rules={[
        {
          required: true,
          message: t("systems.Error.cannotBeEmpty", {
            field: t("domains.Transfer.withdrawAmount"),
          }),
        },
      ]}
      name="amount0"
    >
      <Input
        type="number"
        placeholder={t("systems.Placeholder.pleaseInput", {
          field: t("domains.Transfer.withdrawAmount"),
        })}
        onInput={(e) =>
          handleAmount0Input((e.target as HTMLInputElement).value)
        }
        suffix={
          <Checkbox
            checked={isSyncToAmount}
            onChange={(e) => setIsSyncToAmount(e.target.checked)}
          >
            {t("domains.Transfer.autoSyncToWithdrawUnit")}
          </Checkbox>
        }
      />
    </Form.Item>
  );

  const RateFormItem = () => (
    <Form.Item
      label={t("domains.Transfer.withdrawRate")}
      name="rate"
      initialValue={withdraw_rate.value}
    >
      <Input readOnly type="number" />
    </Form.Item>
  );

  return (
    <div className="w:500 p:16 bg:white r:4 mt:0 mb:0 ml:auto mr:auto">
      <h4 className="mb:24 t:center fg:secondary">
        {t("systems.Menu.createMemberWithdraw")}
      </h4>

      <Spin spinning={withdraw_rate.loading}>
        <Form
          form={form}
          labelCol={{ span: 5 }}
          initialValues={{ rate: withdraw_rate.value }}
          onFinish={handleFinish}
        >
          {isWithdrawRateRequired && <Amount0FormItem />}
          {isWithdrawRateRequired && <RateFormItem />}

          <Form.Item
            label={t("domains.Transfer.withdrawUnit")}
            rules={[{ required: true }]}
            name="amount"
          >
            <Input
              type="number"
              placeholder={t("systems.Placeholder.pleaseInput", {
                field: t("domains.Transfer.withdrawUnit"),
              })}
              onInput={(e) =>
                handleAmountInput((e.target as HTMLInputElement).value)
              }
              suffix={
                isWithdrawRateRequired && (
                  <Checkbox
                    checked={isSyncToAmount0}
                    onChange={(e) => setIsSyncToAmount0(e.target.checked)}
                  >
                    {t("domains.Transfer.autoSyncToWithdrawAmount")}
                  </Checkbox>
                )
              }
            />
          </Form.Item>

          <Form.Item
            label={t("domains.Transfer.orderId")}
            name={["order", "id"]}
          >
            <Input
              placeholder={t("systems.Placeholder.pleaseInput", {
                field: t("domains.Transfer.orderId"),
              })}
            />
          </Form.Item>

          <Form.Item
            label={t("domains.Transfer.memberId")}
            name={["member", "id"]}
          >
            <Input
              placeholder={t("systems.Placeholder.pleaseInput", {
                field: t("domains.Transfer.memberId"),
              })}
            />
          </Form.Item>

          <Form.Item
            label={t("domains.Transfer.memberUsername")}
            name={["member", "username"]}
          >
            <Input
              placeholder={t("systems.Placeholder.pleaseInput", {
                field: t("domains.Transfer.memberUsername"),
              })}
            />
          </Form.Item>

          <Form.Item
            label={t("domains.Transfer.memberName")}
            name={["member", "name"]}
          >
            <Input
              placeholder={t("systems.Placeholder.pleaseInput", {
                field: t("domains.Transfer.memberName"),
              })}
            />
          </Form.Item>

          <Form.Item
            label={t("domains.Transfer.targetWallet")}
            name={["target", "address"]}
            rules={[
              {
                required: true,
                message: t("systems.Error.cannotBeEmpty", {
                  field: t("domains.Transfer.targetWallet"),
                }),
              },
            ]}
          >
            <Input
              placeholder={t("systems.Placeholder.pleaseInput", {
                field: t("domains.Transfer.targetWallet"),
              })}
            />
          </Form.Item>

          <Form.Item label={t("domains.Transfer.note")} name="note">
            <Input
              placeholder={t("systems.Placeholder.pleaseInput", {
                field: t("domains.Transfer.note"),
              })}
            />
          </Form.Item>

          <div className="flex jc:center gap:16">
            <Button type="primary" htmlType="submit" loading={loading}>
              {t("systems.Action.submit")}
            </Button>

            <Button htmlType="reset">{t("systems.Action.clear")}</Button>
          </div>
        </Form>
      </Spin>
    </div>
  );
};
